import React, { useState, useEffect, useRef, useCallback } from "react";
import { useAuth } from "../context/ApiKeyContext";
import {
  PictureAsPdf,
  Description,
  Subject,
  Image as ImageIcon,
  InsertDriveFile,
  Audiotrack,
  Movie,
  GetApp,
} from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";
import {
  Typography,
  Box,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import OptimizedPDFViewer from "./OptimizedPDFViewer";
import { getApiBaseUrl } from "../config";
import { renderAsync } from "docx-preview";
import MediaPreviewDialog from "./MediaPreviewDialog";
import { isAudioFile, isVideoFile } from "./MediaPlayer";
import { useTranslation } from "../hooks/useTranslation";

const API_BASE_URL = getApiBaseUrl();

// Função para truncar nome do arquivo mantendo a extensão
const truncateFilename = (filename, maxLength = 25) => {
  if (!filename || filename.length <= maxLength) {
    return filename;
  }

  const lastDotIndex = filename.lastIndexOf(".");
  if (lastDotIndex === -1) {
    // Sem extensão
    return filename.substring(0, maxLength - 3) + "...";
  }

  const extension = filename.substring(lastDotIndex);
  const nameWithoutExtension = filename.substring(0, lastDotIndex);

  // Se a extensão é muito longa para o espaço disponível, truncar tudo
  if (extension.length >= maxLength - 3) {
    return filename.substring(0, maxLength - 3) + "...";
  }

  // Calcular espaço disponível para o nome (reservando espaço para extensão + "...")
  const ellipsisLength = 3;
  const availableForName = maxLength - extension.length - ellipsisLength;

  if (availableForName <= 0) {
    // Se não há espaço suficiente para nome + "..." + extensão, mostrar só extensão
    return "..." + extension;
  }

  return (
    nameWithoutExtension.substring(0, availableForName) + "..." + extension
  );
};

const useStyles = makeStyles((theme) => ({
  fileItem: {
    display: "inline-flex",
    alignItems: "center",
    marginBottom: theme.spacing(0.25),
    cursor: "pointer",
    padding: theme.spacing(0.5, 0.75),
    borderRadius: theme.spacing(0.5),
    border: "1px solid transparent",
    transition: "background-color 0.12s ease, border-color 0.12s ease",
    maxWidth: "100%",
    "&:hover": {
      backgroundColor: "var(--ikaro-orange-light)",
      borderColor: "var(--ikaro-orange-primary)",
    },
  },
  fileItemNoLink: {
    display: "inline-flex",
    alignItems: "center",
    marginBottom: theme.spacing(0.25),
    padding: theme.spacing(0.5, 0.75),
    borderRadius: theme.spacing(0.5),
    cursor: "default",
    maxWidth: "100%",
  },
  fileIcon: {
    marginRight: theme.spacing(0.75),
    fontSize: "1.05rem",
    color: "var(--ikaro-gray-dark)",
    flexShrink: 0,
  },
  fileName: {
    fontSize: "0.65rem",
    fontWeight: 500,
    textDecoration: "none",
    /* O nome acompanha a cor do ícone (cinza escuro) para ficar mais discreto.
       Como `--ikaro-gray-dark` já é redefinido no dark mode, isso funciona
       corretamente nos dois temas sem necessidade de override. */
    color: "var(--ikaro-gray-dark)",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    "&:hover": {
      textDecoration: "underline",
    },
  },
  fileNameNoLink: {
    fontSize: "0.65rem",
    color: "var(--ikaro-text-primary)",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  floatingAlert: {
    position: "fixed",
    top: 16,
    right: 16,
    zIndex: 9999,
    minWidth: "300px",
    maxWidth: "500px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
    borderRadius: "4px",
    animation: "$slideIn 0.3s ease-out",
  },
  "@keyframes slideIn": {
    from: {
      transform: "translateX(100%)",
      opacity: 0,
    },
    to: {
      transform: "translateX(0)",
      opacity: 1,
    },
  },
  previewDialog: {
    "& .MuiDialog-paper": {
      maxWidth: "90vw",
      maxHeight: "90vh",
      width: "900px",
      height: "700px",
    },
  },
  previewDialogTitle: {
    padding: theme.spacing(1.5, 2),
    background: "var(--ikaro-gradient-banner)",
    borderBottom: "1px solid var(--ikaro-orange-hover)",
    "& .MuiTypography-h6": {
      fontSize: "1rem",
      fontWeight: 600,
      color: "var(--ikaro-text-white)",
    },
  },
  previewContent: {
    height: "100%",
    overflow: "hidden",
    padding: 0, // Remove padding para aproveitar todo o espaço
    "& iframe": {
      width: "100%",
      height: "100%",
      border: "none",
    },
  },
  previewActions: {
    padding: theme.spacing(1.5, 2),
    backgroundColor: "var(--ikaro-bg-surface)",
    borderTop: "1px solid var(--ikaro-border-default)",
    "& .MuiButton-root": {
      color: "var(--ikaro-orange-primary)",
      backgroundColor: "var(--ikaro-bg-surface-hover)",
      fontWeight: 500,
      borderRadius: "6px",
      textTransform: "none",
      border: "1px solid var(--ikaro-gray-medium)",
      "&:hover": {
        backgroundColor: "var(--ikaro-orange-light)",
        borderColor: "var(--ikaro-orange-primary)",
        transform: "translateY(-1px)",
        boxShadow: "var(--ikaro-shadow-light)",
      },
    },
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "200px",
  },
  docxPreviewContent: {
    height: "100%",
    overflow: "auto",
    padding: theme.spacing(1),
    backgroundColor: "#fff",
    "& .docx-preview-wrapper": {
      padding: theme.spacing(2),
    },
  },
  docxContainer: {
    width: "100%",
    minHeight: "200px",
  },
  docxPreviewActions: {
    padding: theme.spacing(1, 2),
    backgroundColor: "var(--ikaro-bg-surface)",
    borderTop: "1px solid var(--ikaro-border-default)",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    "& .MuiButton-root": {
      color: "var(--ikaro-orange-primary)",
      backgroundColor: "var(--ikaro-bg-surface-hover)",
      fontWeight: 500,
      borderRadius: "6px",
      textTransform: "none",
      border: "1px solid var(--ikaro-gray-medium)",
      "&:hover": {
        backgroundColor: "var(--ikaro-orange-light)",
        borderColor: "var(--ikaro-orange-primary)",
        transform: "translateY(-1px)",
        boxShadow: "var(--ikaro-shadow-light)",
      },
    },
  },
  docxDisclaimer: {
    fontSize: "0.7rem",
    color: "var(--ikaro-gray-dark)",
    fontStyle: "italic",
    maxWidth: "70%",
    lineHeight: 1.3,
  },
  /* Modal "sem preview disponível": layout centrado vertical/horizontal,
     ícone grande do tipo, mensagem amigável e botão para baixar. Usado quando
     o arquivo não tem html_preview_token e não é PDF/DOCX/áudio/vídeo. */
  noPreviewDialog: {
    "& .MuiDialog-paper": {
      maxWidth: 460,
      width: "100%",
      borderRadius: theme.spacing(1),
    },
  },
  noPreviewTitle: {
    padding: theme.spacing(1.5, 2),
    background: "var(--ikaro-gradient-banner)",
    borderBottom: "1px solid var(--ikaro-orange-hover)",
    "& .MuiTypography-h6": {
      fontSize: "1rem",
      fontWeight: 600,
      color: "var(--ikaro-text-white)",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
  },
  noPreviewContent: {
    padding: theme.spacing(5, 4),
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    gap: theme.spacing(1.5),
  },
  noPreviewIconWrap: {
    width: 88,
    height: 88,
    borderRadius: "50%",
    backgroundColor: "var(--ikaro-orange-light)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: theme.spacing(0.5),
    "& .MuiSvgIcon-root": {
      fontSize: 44,
      color: "var(--ikaro-orange-primary)",
    },
  },
  noPreviewHeadline: {
    fontSize: "1.05rem",
    fontWeight: 600,
    color: "var(--ikaro-text-primary)",
    margin: 0,
  },
  noPreviewMessage: {
    fontSize: "0.85rem",
    color: "var(--ikaro-text-secondary)",
    lineHeight: 1.45,
    maxWidth: 320,
    margin: 0,
    "& strong": {
      color: "var(--ikaro-text-primary)",
      fontWeight: 600,
      wordBreak: "break-all",
    },
  },
  noPreviewActions: {
    padding: theme.spacing(1.5, 2),
    backgroundColor: "var(--ikaro-bg-surface)",
    borderTop: "1px solid var(--ikaro-border-default)",
    justifyContent: "flex-end",
    gap: theme.spacing(1),
  },
  primaryDownloadButton: {
    backgroundColor: "var(--ikaro-orange-primary)",
    color: "var(--ikaro-text-white)",
    textTransform: "none",
    fontWeight: 600,
    boxShadow: "none",
    "&:hover": {
      backgroundColor: "var(--ikaro-orange-hover)",
      boxShadow: "var(--ikaro-shadow-light)",
    },
  },
  previewActionsWithDownload: {
    justifyContent: "space-between",
    gap: theme.spacing(1),
  },
  previewActionsLeft: {
    display: "inline-flex",
    alignItems: "center",
    gap: theme.spacing(1),
    flex: 1,
    minWidth: 0,
  },
}));

const FileDownload = ({
  file,
  filename,
  token: fileToken,
  html_preview_token,
  previewType,
  serviceName = "DOCUMENT_CREATION",
  className,
  style,
  showError = true,
}) => {
  const classes = useStyles();
  const { token } = useAuth();
  const { t } = useTranslation("common");
  const [error, setError] = useState(null);
  const [downloadingFile, setDownloadingFile] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewContent, setPreviewContent] = useState("");
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);
  const [docxPreviewOpen, setDocxPreviewOpen] = useState(false);
  const [loadingDocxPreview, setLoadingDocxPreview] = useState(false);
  const [mediaPreviewOpen, setMediaPreviewOpen] = useState(false);
  /* Aberto quando o arquivo não tem qualquer rota de preview disponível
     (sem html_preview_token e não é PDF/DOCX/áudio/vídeo). Exibe uma
     mensagem amigável e oferece o download direto. */
  const [noPreviewOpen, setNoPreviewOpen] = useState(false);
  const docxContainerRef = useRef(null);

  // Determinar filename, token e html_preview_token a partir dos props
  const finalFilename = file?.filename || filename;
  const finalToken = file?.token || fileToken;
  const finalHtmlPreviewToken = file?.html_preview_token || html_preview_token;

  // Limpar erro automaticamente após 5 segundos
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError(null);
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [error]);

  // Função para verificar se o arquivo é PDF
  const isPdfFile = (filename) => {
    if (!filename) return false;
    const extension = filename.split(".").pop().toLowerCase();
    return extension === "pdf";
  };

  const isDocxFile = (filename) => {
    if (!filename) return false;
    const extension = filename.split(".").pop().toLowerCase();
    return extension === "docx" || extension === "doc";
  };

  const isAudioFilename = (filename) => isAudioFile(filename);
  const isVideoFilename = (filename) => isVideoFile(filename);

  // Função para obter o ícone apropriado baseado na extensão do arquivo
  const getFileIcon = (filename) => {
    if (!filename) return <InsertDriveFile className={classes.fileIcon} />;

    const extension = filename.split(".").pop().toLowerCase();

    switch (extension) {
      case "pdf":
        return <PictureAsPdf className={classes.fileIcon} />;
      case "docx":
      case "doc":
        return <Description className={classes.fileIcon} />;
      case "txt":
        return <Subject className={classes.fileIcon} />;
      case "png":
      case "jpg":
      case "jpeg":
      case "gif":
        return <ImageIcon className={classes.fileIcon} />;
      default:
        if (isAudioFilename(filename)) {
          return <Audiotrack className={classes.fileIcon} />;
        }
        if (isVideoFilename(filename)) {
          return <Movie className={classes.fileIcon} />;
        }
        return <InsertDriveFile className={classes.fileIcon} />;
    }
  };

  // Função para fazer download do arquivo
  const handleDownload = async (fileToken, filename) => {
    if (!fileToken || !token) return;

    try {
      setError(null);
      setDownloadingFile(fileToken);

      const downloadUrl = `${API_BASE_URL}/files/download?token=${fileToken}&Servicename=${encodeURIComponent(serviceName)}`;

      const response = await fetch(downloadUrl, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          accept: "application/octet-stream",
        },
      });

      if (!response.ok) {
        throw new Error(
          `Erro ao fazer download do arquivo: ${response.status} - ${response.statusText}`,
        );
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename || "arquivo";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Erro ao fazer download:", error);
      setError(
        `Falha ao fazer download do arquivo "${filename}": ${error.message}`,
      );
    } finally {
      setDownloadingFile(null);
    }
  };

  // Função para buscar o conteúdo HTML do preview
  const handlePreview = async (htmlPreviewToken, filename) => {
    if (!htmlPreviewToken || !token) return;

    try {
      setError(null);
      setLoadingPreview(true);
      setPreviewOpen(true);

      const previewUrl = `${API_BASE_URL}/files/download?token=${htmlPreviewToken}&Servicename=${encodeURIComponent(serviceName)}`;

      const response = await fetch(previewUrl, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          accept: "text/html",
        },
      });

      if (!response.ok) {
        throw new Error(
          `Erro ao buscar preview do arquivo: ${response.status} - ${response.statusText}`,
        );
      }

      const htmlContent = await response.text();
      setPreviewContent(htmlContent);
    } catch (error) {
      console.error("Erro ao buscar preview:", error);
      setError(
        `Falha ao carregar preview do arquivo "${filename}": ${error.message}`,
      );
      setPreviewOpen(false);
    } finally {
      setLoadingPreview(false);
    }
  };

  // Função para fechar o preview
  const handleClosePreview = () => {
    setPreviewOpen(false);
    setPreviewContent("");
  };

  // Função para abrir preview de PDF otimizado
  const handlePdfPreview = (fileToken, filename) => {
    if (!fileToken || !token) return;
    setPdfPreviewOpen(true);
  };

  // Função para fechar o preview de PDF
  const handleClosePdfPreview = () => {
    setPdfPreviewOpen(false);
  };

  const handleDocxPreview = useCallback(
    async (fileToken, filename) => {
      if (!fileToken || !token) return;

      try {
        setError(null);
        setLoadingDocxPreview(true);
        setDocxPreviewOpen(true);

        const downloadUrl = `${API_BASE_URL}/files/download?token=${fileToken}&Servicename=${encodeURIComponent(serviceName)}`;

        const response = await fetch(downloadUrl, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            accept: "application/octet-stream",
          },
        });

        if (!response.ok) {
          throw new Error(
            `Erro ao buscar preview do documento: ${response.status} - ${response.statusText}`,
          );
        }

        const blob = await response.blob();

        // Espera o próximo render para garantir que o container está no DOM
        requestAnimationFrame(() => {
          if (docxContainerRef.current) {
            docxContainerRef.current.innerHTML = "";
            renderAsync(blob, docxContainerRef.current, null, {
              className: "docx-preview-wrapper",
              inWrapper: true,
              ignoreWidth: false,
              ignoreHeight: true,
              ignoreFonts: false,
              breakPages: true,
              renderHeaders: true,
              renderFooters: true,
              renderFootnotes: true,
            }).catch((err) => {
              console.error("Erro ao renderizar docx:", err);
              setError(
                `Falha ao renderizar preview do documento "${filename}": ${err.message}`,
              );
            });
          }
        });
      } catch (error) {
        console.error("Erro ao buscar docx para preview:", error);
        setError(
          `Falha ao carregar preview do documento "${filename}": ${error.message}`,
        );
        setDocxPreviewOpen(false);
      } finally {
        setLoadingDocxPreview(false);
      }
    },
    [token, serviceName],
  );

  const handleCloseDocxPreview = () => {
    setDocxPreviewOpen(false);
    if (docxContainerRef.current) {
      docxContainerRef.current.innerHTML = "";
    }
  };

  if (!finalFilename) return null;

  const hasToken = finalToken && finalToken.trim() !== "";
  const isDownloading = downloadingFile === finalToken;

  // Truncar nome do arquivo para exibição
  const displayFilename = isDownloading
    ? `Baixando ${truncateFilename(finalFilename)}...`
    : truncateFilename(finalFilename);

  // Nome completo para o tooltip
  const fullDisplayName = isDownloading
    ? `Baixando ${finalFilename}...`
    : finalFilename;

  const resolvedPreviewType =
    previewType ||
    (isPdfFile(finalFilename)
      ? "pdf"
      : isDocxFile(finalFilename)
        ? "docx"
        : isAudioFilename(finalFilename)
          ? "audio"
          : isVideoFilename(finalFilename)
            ? "video"
            : null);

  /* Clique no arquivo agora abre a melhor pré-visualização disponível.
     O download continua acessível, mas a partir do botão dentro do modal
     de preview, inclusive no modal "sem preview disponível" para tipos
     que não conseguimos renderizar inline. */
  const handleClick = (e) => {
    if (!hasToken) return;

    e.preventDefault();
    e.stopPropagation();

    if (loadingPreview || loadingDocxPreview) return;

    if (resolvedPreviewType === "pdf" && finalToken) {
      handlePdfPreview(finalToken, finalFilename);
    } else if (resolvedPreviewType === "docx" && finalToken) {
      handleDocxPreview(finalToken, finalFilename);
    } else if (
      (resolvedPreviewType === "audio" || resolvedPreviewType === "video") &&
      finalToken
    ) {
      setMediaPreviewOpen(true);
    } else if (finalHtmlPreviewToken) {
      handlePreview(finalHtmlPreviewToken, finalFilename);
    } else {
      /* Sem rota de preview: abre o modal "sem preview" para que o usuário
         consiga baixar e abrir localmente. */
      setNoPreviewOpen(true);
    }
  };

  /* Download direto disparado a partir de um botão dentro de um modal.
     Mantém compatibilidade com o estado de "Baixando..." no item. */
  const handleDownloadFromDialog = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (!finalToken || isDownloading) return;
    handleDownload(finalToken, finalFilename);
  };

  return (
    <>
      {/* Alerta flutuante */}
      {showError && error && (
        <Alert
          severity="error"
          className={classes.floatingAlert}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {/* Modal de Preview PDF */}
      <Dialog
        open={pdfPreviewOpen}
        onClose={handleClosePdfPreview}
        className={classes.previewDialog}
        maxWidth={false}
      >
        <DialogTitle className={classes.previewDialogTitle}>
          {finalFilename}
        </DialogTitle>
        <DialogContent className={classes.previewContent}>
          <OptimizedPDFViewer
            token={finalToken}
            width="100%"
            height="100%"
            fileName={finalFilename}
          />
        </DialogContent>
        <DialogActions className={classes.previewActions}>
          <Button
            onClick={handleDownloadFromDialog}
            startIcon={<GetApp />}
            disabled={isDownloading}
          >
            {t("filePreview.downloadFile")}
          </Button>
          <Button onClick={handleClosePdfPreview}>{t("close")}</Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Preview HTML */}
      <Dialog
        open={previewOpen}
        onClose={handleClosePreview}
        className={classes.previewDialog}
        maxWidth={false}
      >
        <DialogTitle className={classes.previewDialogTitle}>
          {finalFilename}
        </DialogTitle>
        <DialogContent className={classes.previewContent}>
          {loadingPreview ? (
            <div className={classes.loadingContainer}>
              <CircularProgress />
            </div>
          ) : (
            <iframe
              srcDoc={previewContent}
              title={`Preview de ${finalFilename}`}
            />
          )}
        </DialogContent>
        <DialogActions className={classes.previewActions}>
          <Button
            onClick={handleDownloadFromDialog}
            startIcon={<GetApp />}
            disabled={isDownloading}
          >
            {t("filePreview.downloadFile")}
          </Button>
          <Button onClick={handleClosePreview}>{t("close")}</Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Preview DOCX */}
      <Dialog
        open={docxPreviewOpen}
        onClose={handleCloseDocxPreview}
        className={classes.previewDialog}
        maxWidth={false}
      >
        <DialogTitle className={classes.previewDialogTitle}>
          {finalFilename}
        </DialogTitle>
        <DialogContent className={classes.docxPreviewContent}>
          {loadingDocxPreview && (
            <div className={classes.loadingContainer}>
              <CircularProgress />
            </div>
          )}
          <div ref={docxContainerRef} className={classes.docxContainer} />
        </DialogContent>
        <DialogActions
          className={`${classes.docxPreviewActions} ${classes.previewActionsWithDownload}`}
        >
          <Typography className={classes.docxDisclaimer}>
            Esta é uma pré-visualização aproximada. Para uma visualização fiel
            ao documento original, faça o download e abra no seu editor de
            texto.
          </Typography>
          <Box display="inline-flex" style={{ gap: 8 }}>
            <Button
              onClick={handleDownloadFromDialog}
              startIcon={<GetApp />}
              disabled={isDownloading}
            >
              {t("filePreview.downloadFile")}
            </Button>
            <Button onClick={handleCloseDocxPreview}>{t("close")}</Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Modal de Preview Áudio/Vídeo */}
      {(resolvedPreviewType === "audio" || resolvedPreviewType === "video") && (
        <MediaPreviewDialog
          open={mediaPreviewOpen}
          onClose={() => setMediaPreviewOpen(false)}
          token={finalToken}
          filename={finalFilename}
          serviceName={serviceName}
          mediaType={resolvedPreviewType}
          autoPlay
          onDownload={handleDownloadFromDialog}
        />
      )}

      {/* Modal "Sem preview disponível": layout centrado com ícone grande,
          mensagem e botão de download em destaque. */}
      <Dialog
        open={noPreviewOpen}
        onClose={() => setNoPreviewOpen(false)}
        className={classes.noPreviewDialog}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle className={classes.noPreviewTitle}>
          {finalFilename}
        </DialogTitle>
        <DialogContent className={classes.noPreviewContent}>
          <Box className={classes.noPreviewIconWrap}>
            {React.cloneElement(getFileIcon(finalFilename), {
              className: undefined,
            })}
          </Box>
          <Typography component="h3" className={classes.noPreviewHeadline}>
            {t("filePreview.noPreviewTitle")}
          </Typography>
          <Typography component="p" className={classes.noPreviewMessage}>
            {t("filePreview.noPreviewForFile", {
              filename: finalFilename,
            })}
          </Typography>
          <Typography component="p" className={classes.noPreviewMessage}>
            {t("filePreview.noPreviewHint")}
          </Typography>
        </DialogContent>
        <DialogActions className={classes.noPreviewActions}>
          <Button onClick={() => setNoPreviewOpen(false)}>{t("close")}</Button>
          <Button
            variant="contained"
            disableElevation
            className={classes.primaryDownloadButton}
            startIcon={<GetApp />}
            onClick={handleDownloadFromDialog}
            disabled={isDownloading}
          >
            {t("filePreview.downloadFile")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Item do arquivo — clique abre o preview (ou modal de download
          quando não é possível pré-visualizar). */}
      <Box
        className={hasToken ? classes.fileItem : classes.fileItemNoLink}
        onClick={handleClick}
        style={{
          cursor: hasToken
            ? isDownloading
              ? "not-allowed"
              : "pointer"
            : "default",
          opacity: isDownloading ? 0.6 : 1,
          ...style,
        }}
      >
        {getFileIcon(finalFilename)}

        <Tooltip title={fullDisplayName} placement="top" arrow>
          <Typography
            component="span"
            className={hasToken ? classes.fileName : classes.fileNameNoLink}
            style={{
              color:
                hasToken && isDownloading
                  ? "var(--ikaro-gray-dark)"
                  : undefined,
            }}
          >
            {displayFilename}
          </Typography>
        </Tooltip>
      </Box>
    </>
  );
};

export default FileDownload;
